/**
 * Paper Trading Account Utilities
 * 
 * Provides utilities for managing the Manual Journal paper trading account
 * which allows users to do fully manual journaling without a broker connection.
 */

import type { PrismaClient } from '@prisma/client';

// Constants
export const PAPER_ACCOUNT_PREFIX = 'MANUAL_JOURNAL_';
export const DEFAULT_INITIAL_BALANCE = 100000;
export const PAPER_ACCOUNT_BROKER_NAME = 'Paper Trading';
export const PAPER_ACCOUNT_DEFAULT_NAME = 'Manual Journal';

/**
 * Generate a paper account ID from the database record ID
 * Format: MANUAL_JOURNAL_{cuid}
 */
export function getPaperAccountId(paperAccountDbId: string): string {
  return `${PAPER_ACCOUNT_PREFIX}${paperAccountDbId}`;
}

/**
 * Check if an account ID is a paper trading account
 */
export function isPaperAccount(accountId: string | null | undefined): boolean {
  return accountId?.startsWith(PAPER_ACCOUNT_PREFIX) ?? false;
}

/**
 * Extract the database ID from a paper account ID
 * Returns null if not a valid paper account ID
 */
export function extractPaperAccountDbId(accountId: string): string | null {
  if (!isPaperAccount(accountId)) return null;
  return accountId.replace(PAPER_ACCOUNT_PREFIX, '');
}

/**
 * Get paper account configuration from database
 * Returns null if user has no paper account configured
 */
export async function getPaperAccountConfig(
  prisma: PrismaClient,
  userId: string
): Promise<{
  id: string;
  accountName: string;
  initialBalance: number;
  status: string;
} | null> {
  const account = await prisma.paperTradingAccount.findUnique({
    where: { userId },
    select: {
      id: true,
      accountName: true,
      initialBalance: true,
      status: true,
    },
  });
  return account;
}

/**
 * Get paper account info for display in account dropdowns
 * Returns null if user has no active paper account
 */
export async function getPaperAccountForUser(
  prisma: PrismaClient,
  userId: string
): Promise<{ id: string; name: string; broker: string } | null> {
  const account = await prisma.paperTradingAccount.findUnique({
    where: { userId },
    select: {
      id: true,
      accountName: true,
      status: true,
    },
  });

  if (!account || account.status !== 'ACTIVE') {
    return null;
  }

  return {
    id: getPaperAccountId(account.id),
    name: account.accountName,
    broker: PAPER_ACCOUNT_BROKER_NAME,
  };
}

/**
 * Validate that a paper account ID belongs to a specific user
 * Used for security checks before allowing operations on paper accounts
 */
export async function validatePaperAccountOwnership(
  prisma: PrismaClient,
  accountId: string,
  userId: string
): Promise<boolean> {
  if (!isPaperAccount(accountId)) return false;

  const dbId = extractPaperAccountDbId(accountId);
  if (!dbId) return false;

  const account = await prisma.paperTradingAccount.findFirst({
    where: {
      id: dbId,
      userId,
      status: 'ACTIVE',
    },
    select: { id: true },
  });

  return !!account;
}

/**
 * Get the initial balance for a paper account
 * Returns default balance if account not found
 */
export async function getPaperAccountInitialBalance(
  prisma: PrismaClient,
  accountId: string
): Promise<number> {
  if (!isPaperAccount(accountId)) {
    return 0;
  }

  const dbId = extractPaperAccountDbId(accountId);
  if (!dbId) {
    return DEFAULT_INITIAL_BALANCE;
  }

  const account = await prisma.paperTradingAccount.findUnique({
    where: { id: dbId },
    select: { initialBalance: true },
  });

  return account?.initialBalance ?? DEFAULT_INITIAL_BALANCE;
}
